import { action, makeObservable, observable, runInAction } from "mobx";
import PropertyAPI from "../api/PropertyAPI";
import {
  Amenity,
  City,
  EventType,
  PropertyCategory,
  PropertyType,
  PropertyDetails,
  PropertyListItem,
} from "../models/Property/Property";
import {
  GetListParams,
} from "../models/Property/PropertyParams";
import { PropertyFilters } from "../models/Property/PropertyFilters";

export class PropertyStore {
  propertyAPI: PropertyAPI;
  eventTypes: EventType[];
  propertyCategories: PropertyCategory[];
  propertyTypes: PropertyType[];
  amenities: Amenity[];
  loading: boolean;
  cities: City[];
  properties: PropertyListItem[];
  total_count: number;
  total_pages: number;
  propertyDetails: PropertyDetails | null;

  constructor() {
    this.propertyAPI = new PropertyAPI();
    this.eventTypes = [];
    this.propertyCategories = [];
    this.propertyTypes = [];
    this.amenities = [];
    this.loading = false;
    this.cities = [];
    this.properties = [];
    this.total_count = 0;
    this.total_pages = 0;
    this.propertyDetails = null;
    makeObservable(this, {
      eventTypes: observable,
      propertyCategories: observable,
      propertyTypes: observable,
      cities: observable,
      amenities: observable,
      loading: observable,
      properties: observable,
      total_count: observable,
      total_pages: observable,
      propertyDetails: observable,
      getEventTypes: action,
      getPropertyCategories: action,
      getPropertyTypes: action,
      getAmenities: action,
      createAmenity: action,
      createProperty: action,
      getProperties: action,
      getPropertyById: action,
      updateProperty: action,
      clearPropertyDetails: action,
      clearCities: action,
      clearDropdownData: action,
      setCities: action,
    });
  }

  getProperties = async (filters: PropertyFilters = {}) => {
    runInAction(() => {
      this.loading = true;
    });
    try {
      const response = await this.propertyAPI.getProperties(filters);
      const result = response?.result;
      runInAction(() => {
        if (result && typeof result === "object") {
          this.properties = Array.isArray(result.list) ? result.list : [];
          this.total_count = Number(result.total_count) ?? 0;
          this.total_pages = Number(result.total_pages) ?? 0;
        } else {
          this.properties = [];
          this.total_count = 0;
          this.total_pages = 0;
        }
      });
      return response;
    } catch (error) {
      runInAction(() => {
        this.properties = [];
        this.total_count = 0;
        this.total_pages = 0;
      });
      throw error;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  getPropertyById = async (id: number) => {
    runInAction(() => {
      this.loading = true;
    });
    try {
      const response = await this.propertyAPI.getPropertyById(id);
      runInAction(() => {
        this.propertyDetails = (response?.result ?? null) as PropertyDetails | null;
      });
      return response;
    } catch (error) {
      runInAction(() => {
        this.propertyDetails = null;
      });
      throw error;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  updateProperty = async (id: number, body: Record<string, unknown>) => {
    runInAction(() => {
      this.loading = true;
    });
    try {
      const response = await this.propertyAPI.updateProperty(id, body);
      return response;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  clearPropertyDetails = () => {
    this.propertyDetails = null;
  };

  getEventTypes = async (params: GetListParams = {}) => {
    try {
      const list = await this.propertyAPI.getEventTypes({
        ...params,
        active: params.active !== false,
      });
      runInAction(() => {
        this.eventTypes = Array.isArray(list) ? list : [];
      });
      return this.eventTypes;
    } catch (error) {
      runInAction(() => {
        this.eventTypes = [];
      });
      throw error;
    }
  };

  getPropertyCategories = async (params: GetListParams = {}) => {
    try {
      const list = await this.propertyAPI.getPropertyCategories({
        ...params,
        active: params.active !== false,
      });
      runInAction(() => {
        this.propertyCategories = Array.isArray(list) ? list : [];
      });
      return this.propertyCategories;
    } catch (error) {
      runInAction(() => {
        this.propertyCategories = [];
      });
      throw error;
    }
  };

  getPropertyTypes = async (params: GetListParams = {}) => {
    try {
      const list = await this.propertyAPI.getPropertyTypes({
        ...params,
        active: params.active !== false,
      });
      runInAction(() => {
        this.propertyTypes = Array.isArray(list) ? list : [];
      });
      return this.propertyTypes;
    } catch (error) {
      runInAction(() => {
        this.propertyTypes = [];
      });
      throw error;
    }
  };

  getAmenities = async (
    params: GetListParams & { search_text?: string } = {}
  ) => {
    try {
      const list = await this.propertyAPI.getAmenities({
        ...params,
        active: params.active !== false,
      });
      runInAction(() => {
        this.amenities = Array.isArray(list) ? list : [];
      });
      return this.amenities;
    } catch (error) {
      runInAction(() => {
        this.amenities = [];
      });
      throw error;
    }
  };

  createAmenity = async (name: string) => {
    runInAction(() => {
      this.loading = true;
    });
    try {
      const response = await this.propertyAPI.createAmenity(name);
      const created = response?.result;
      if (created && created.id != null) {
        runInAction(() => {
          this.amenities = [...this.amenities, created];
        });
        return response;
      }
      return response;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  createProperty = async (body: Record<string, unknown>) => {
    runInAction(() => {
      this.loading = true;
    });
    try {
      const response = await this.propertyAPI.createProperty(body);
      return response;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  setCities = (cities: City[]) => {
    this.cities = Array.isArray(cities) ? cities : [];
  };

  clearCities = () => {
    this.cities = [];
  };

  clearDropdownData = () => {
    this.cities = [];
    this.eventTypes = [];
    this.propertyCategories = [];
    this.propertyTypes = [];
    this.amenities = [];
  };
}
